'use client';

import {
  ControlType,
  CalculatorId,
  CalculatorType,
  ControlIds,
  type CalculatorConfig,
  DataTableInputMode,
  type MultiColumnSelectControl,
  type ControlValues
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { ChiSquareCalculator } from './chi-square-calculator';
import { calculateChiSquare } from '../../../lib/stats';

interface ChiSquareControlValues extends ControlValues {
  [ControlIds.Values]: string[];
  [ControlIds.DataColumn]: string[];
}

const observedFrequencies: MultiColumnSelectControl = {
  id: ControlIds.Values,
  type: ControlType.MultiColumnSelect,
  label: 'Observed frequencies',
  required: true,
  numericOnly: true,
  minSelections: 1,
  maxSelections: 1
};

const expectedFrequencies: MultiColumnSelectControl = {
  id: ControlIds.DataColumn,
  type: ControlType.MultiColumnSelect,
  label: 'Expected frequencies',
  required: true,
  numericOnly: true,
  minSelections: 1,
  maxSelections: 1
};

export const chiSquareControls = {
  tabs: [
    {
      title: 'Parameters',
      controls: [
        observedFrequencies,
        expectedFrequencies
      ] as const
    }
  ]
};

export const chiSquareConfig: CalculatorConfig<CalculatorId.CHI_SQUARE> = {
  id: CalculatorId.CHI_SQUARE,
  type: CalculatorType.Graph,
  name: 'Chi-square test',
  dataTableInputMode: DataTableInputMode.Required,
  initialData: TableDataJSON.from({
    columns: [
      { id: 'category', name: 'Category', values: ['A', 'B', 'C', 'D'] },
      { id: 'observed', name: 'Observed', values: ['10', '15', '20', '15'] },
      { id: 'expected', name: 'Expected', values: ['15', '15', '15', '15'] }
    ]
  }),
  initialControlValues: {
    [ControlIds.Values]: ['observed'],
    [ControlIds.DataColumn]: ['expected']
  },
  controls: chiSquareControls,
  getInitialConfig: (data: TableDataJSON) => {
    // Find the first numeric column for observed frequencies
    const observedColumn = data.columns.find(col => 
      col.values.every(val => !isNaN(Number(val)))
    )?.id;

    // Find another numeric column for expected frequencies
    const expectedColumn = data.columns.find(col => 
      col.id !== observedColumn && 
      col.values.every(val => !isNaN(Number(val)))
    )?.id;

    return {
      [ControlIds.Values]: observedColumn ? [observedColumn] : [],
      [ControlIds.DataColumn]: expectedColumn ? [expectedColumn] : []
    };
  },
  getVisualizationData: ({ data, controls }) => {
    const values = controls[ControlIds.Values] as string[];
    const observedColumn = values[0];
    if (!observedColumn) return null;

    const observedCol = data.columns.find(col => col.id === observedColumn);
    if (!observedCol) return null;
    const observed = observedCol.values.map(Number);
    if (observed.some(isNaN)) return null;

    const dataColumn = controls[ControlIds.DataColumn] as string[];
    const expectedColumn = dataColumn[0];
    if (!expectedColumn) return null;

    const expectedCol = data.columns.find(col => col.id === expectedColumn);
    if (!expectedCol) return null;
    const expected = expectedCol.values.map(Number);
    if (expected.some(isNaN)) return null;

    if (observed.length !== expected.length) return null;

    const { statistic, pValue } = calculateChiSquare({ observed, expected });
    const degreesOfFreedom = observed.length - 1;

    return {
      statistic,
      pValue,
      degreesOfFreedom,
      observed,
      expected
    };
  },
  render: ({ visualizationData }) => <ChiSquareCalculator visualizationData={visualizationData} />
}; 